import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'
import { Send, MessageSquare, MoreVertical, Pencil, Trash2, X, Check, History, Bot, Globe, ChevronDown } from 'lucide-react'

const FIFTEEN_MIN = 15 * 60 * 1000
const BOT_ID = '__bot__'

/* ─── helpers ─── */
const fmtTime = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

const fmtDate = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return 'Oggi'
  if (d.toDateString() === yesterday.toDateString()) return 'Ieri'
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
}

/* ─── Avatar ─── */
const Avatar = ({ profile, size = 'sm' }) => {
  const dim = size === 'md' ? 'w-9 h-9 text-sm' : 'w-8 h-8 text-xs'
  const name = profile?.nickname || profile?.full_name || '?'
  const letter = name[0]?.toUpperCase() ?? '?'
  const url = profile?.avatar_url ? `${profile.avatar_url}?t=0` : null
  return (
    <div className={`${dim} rounded-full bg-violet-600 flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0`}>
      {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : letter}
    </div>
  )
}

/* ─── Markdown renderer per messaggi bot ─── */
const renderInline = (text) => {
  if (!text) return null
  // Split su **bold**, *italic*, `code`
  const parts = text.split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i} className="font-semibold text-indigo-50">{part.slice(2, -2)}</strong>
    if (part.startsWith('*') && part.endsWith('*'))
      return <em key={i} className="italic opacity-90">{part.slice(1, -1)}</em>
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={i} className="bg-indigo-900/50 text-indigo-200 px-1 py-0.5 rounded text-[11px] font-mono">{part.slice(1, -1)}</code>
    return part
  })
}

const BotMarkdown = ({ content }) => {
  if (!content) return null
  const lines = content.split('\n')
  const elements = []
  let i = 0
  let key = 0

  while (i < lines.length) {
    const line = lines[i]

    // ── Code block ──
    if (line.startsWith('```')) {
      const codeLines = []
      i++
      while (i < lines.length && !lines[i].startsWith('```')) { codeLines.push(lines[i]); i++ }
      elements.push(
        <pre key={key++} className="bg-gray-900 border border-indigo-500/20 rounded-lg px-3 py-2.5 my-2 overflow-x-auto">
          <code className="text-green-300 text-[11px] font-mono leading-relaxed">{codeLines.join('\n')}</code>
        </pre>
      )
      i++; continue
    }

    // ── Headers ──
    if (line.startsWith('### ')) {
      elements.push(<p key={key++} className="font-semibold text-indigo-200 text-sm mt-2 mb-0.5">{renderInline(line.slice(4))}</p>)
      i++; continue
    }
    if (line.startsWith('## ') || line.startsWith('# ')) {
      const lvl = line.startsWith('## ') ? 3 : 2
      const txt = line.slice(lvl)
      elements.push(<p key={key++} className="font-bold text-white text-sm mt-2.5 mb-1 border-b border-indigo-500/20 pb-1">{renderInline(txt)}</p>)
      i++; continue
    }

    // ── Bullet list ──
    if (/^[-*•]\s/.test(line)) {
      const items = []
      while (i < lines.length && /^[-*•]\s/.test(lines[i])) {
        const txt = lines[i].replace(/^[-*•]\s+/, '')
        items.push(
          <li key={i} className="flex gap-2 items-start">
            <span className="text-indigo-400 flex-shrink-0 mt-0.5">•</span>
            <span>{renderInline(txt)}</span>
          </li>
        )
        i++
      }
      elements.push(<ul key={key++} className="space-y-1 my-1.5 ml-0.5 text-sm">{items}</ul>)
      continue
    }

    // ── Numbered list ──
    if (/^\d+\.\s/.test(line)) {
      const items = []
      let n = 1
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const txt = lines[i].replace(/^\d+\.\s+/, '')
        items.push(
          <li key={i} className="flex gap-2 items-start">
            <span className="text-indigo-400 font-mono text-[11px] flex-shrink-0 mt-0.5 min-w-[14px]">{n}.</span>
            <span>{renderInline(txt)}</span>
          </li>
        )
        i++; n++
      }
      elements.push(<ol key={key++} className="space-y-1 my-1.5 ml-0.5 text-sm">{items}</ol>)
      continue
    }

    // ── Divider ──
    if (line.trim() === '---' || line.trim() === '***') {
      elements.push(<hr key={key++} className="border-indigo-500/20 my-2" />)
      i++; continue
    }

    // ── Riga vuota ──
    if (line.trim() === '') {
      if (elements.length > 0) elements.push(<div key={key++} className="h-1" />)
      i++; continue
    }

    // ── Paragrafo normale ──
    elements.push(<p key={key++} className="leading-relaxed text-sm">{renderInline(line)}</p>)
    i++
  }

  return <div className="space-y-0.5">{elements}</div>
}

/* ─── BotAvatar ─── */
const BotAvatar = ({ size = 'sm' }) => {
  const dim = size === 'md' ? 'w-9 h-9 text-lg' : 'w-8 h-8 text-base'
  return (
    <div className={`${dim} rounded-full bg-indigo-600/30 border border-indigo-500/50 flex items-center justify-center flex-shrink-0`}>
      <Bot size={size === 'md' ? 18 : 15} className="text-indigo-300" />
    </div>
  )
}

/* ─── ConversationItem ─── */
const ConversationItem = ({ conv, selected, myId, onClick }) => {
  const other = conv.other
  const nome = other?.nickname || other?.full_name || 'Utente'
  const lastMsg = conv.lastMessage
  const unread = conv.unread > 0

  const lastText = lastMsg?.is_deleted
    ? '🚫 Messaggio eliminato'
    : lastMsg?.contenuto || ''

  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
        selected ? 'bg-violet-600/20 border-l-2 border-violet-500' : 'hover:bg-gray-800/60 border-l-2 border-transparent'
      }`}>
      <Avatar profile={other} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm font-medium truncate ${unread ? 'text-white' : 'text-gray-300'}`}>{nome}</span>
          {lastMsg && <span className="text-xs text-gray-600 flex-shrink-0">{fmtTime(lastMsg.created_at)}</span>}
        </div>
        {lastMsg && (
          <p className={`text-xs truncate mt-0.5 ${unread ? 'text-gray-300' : 'text-gray-500'}`}>
            {!lastMsg.is_deleted && lastMsg.sender_id === myId ? 'Tu: ' : ''}{lastText}
          </p>
        )}
      </div>
      {unread > 0 && (
        <span className="text-xs bg-violet-600 text-white rounded-full px-1.5 py-0.5 min-w-[20px] text-center flex-shrink-0">
          {conv.unread}
        </span>
      )}
    </button>
  )
}

/* ─── BotConversationItem ─── */
const BotConversationItem = ({ selected, unread, lastMsg, onClick }) => (
  <button onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
      selected ? 'bg-indigo-600/20 border-l-2 border-indigo-500' : 'hover:bg-gray-800/60 border-l-2 border-transparent'
    }`}>
    <BotAvatar size="md" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm font-medium truncate ${unread > 0 ? 'text-white' : 'text-gray-300'}`}>
          PlutonicBot
        </span>
        {lastMsg && <span className="text-xs text-gray-600 flex-shrink-0">{fmtTime(lastMsg.created_at)}</span>}
      </div>
      <p className={`text-xs truncate mt-0.5 ${unread > 0 ? 'text-indigo-300' : 'text-gray-500'}`}>
        {lastMsg ? (lastMsg.contenuto?.split('\n')[0] || 'Notifiche automatiche') : 'Notifiche automatiche'}
      </p>
    </div>
    {unread > 0 && (
      <span className="text-xs bg-indigo-600 text-white rounded-full px-1.5 py-0.5 min-w-[20px] text-center flex-shrink-0">
        {unread}
      </span>
    )}
  </button>
)

/* ─── HistoryModal ─── */
const HistoryModal = ({ history, onClose }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" onClick={onClose}>
    <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <History size={15} className="text-violet-400" />
          Storico modifiche
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={16} /></button>
      </div>
      <div className="space-y-3 max-h-72 overflow-y-auto">
        {history.map((h, i) => (
          <div key={h.id} className="bg-gray-800 rounded-xl px-3 py-2.5">
            <p className="text-gray-400 text-xs mb-1">{i === 0 ? 'Versione precedente più recente' : `Versione ${history.length - i}`} · {fmtTime(h.edited_at)}</p>
            <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{h.old_content}</p>
          </div>
        ))}
        {history.length === 0 && <p className="text-gray-600 text-sm text-center py-4">Nessuna modifica trovata.</p>}
      </div>
    </div>
  </div>
)

/* ─── MessageBubble ─── */
const MessageBubble = ({ msg, isMine, isBot, showDate, dateLabel, onDeleteAll, onDeleteMe, onStartEdit, onShowHistory, isEditing, editText, onEditChange, onEditSave, onEditCancel }) => {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef(null)
  const canDeleteAll = isMine && !msg.is_deleted && (Date.now() - new Date(msg.created_at).getTime() < FIFTEEN_MIN)

  useEffect(() => {
    if (!showMenu) return
    const handleClick = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false) }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMenu])

  /* ── Bot message (speciale) ── */
  if (isBot) {
    return (
      <>
        {showDate && (
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-800" />
            <span className="text-xs text-gray-600 flex-shrink-0">{dateLabel}</span>
            <div className="flex-1 h-px bg-gray-800" />
          </div>
        )}
        <div className="flex gap-2.5 items-end">
          <BotAvatar size="sm" />
          <div className="max-w-xs sm:max-w-md lg:max-w-lg bg-indigo-950/60 border border-indigo-500/20 text-indigo-100 px-4 py-2.5 rounded-2xl rounded-bl-sm break-words">
            <p className="text-[10px] text-indigo-400 font-semibold mb-1.5 tracking-wide uppercase">PlutonicBot</p>
            <BotMarkdown content={msg.contenuto} />
            <div className="text-[10px] text-indigo-400/60 text-right mt-1.5">{fmtTime(msg.created_at)}</div>
          </div>
        </div>
      </>
    )
  }

  /* ── Messaggio normale ── */
  return (
    <>
      {showDate && (
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-gray-800" />
          <span className="text-xs text-gray-600 flex-shrink-0">{dateLabel}</span>
          <div className="flex-1 h-px bg-gray-800" />
        </div>
      )}
      <div className={`flex gap-2 items-end group ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>

        {!msg.is_deleted && (
          <div className={`self-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity relative ${isMine ? 'order-first' : 'order-last'}`} ref={menuRef}>
            <button
              onClick={() => setShowMenu(v => !v)}
              className="w-7 h-7 rounded-full hover:bg-gray-800 flex items-center justify-center text-gray-500 hover:text-white transition-colors">
              <MoreVertical size={15} />
            </button>
            {showMenu && (
              <div className={`absolute bottom-full mb-1 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-20 overflow-hidden min-w-[170px] ${isMine ? 'right-0' : 'left-0'}`}>
                {isMine && !msg.is_deleted && (
                  <button
                    onClick={() => { setShowMenu(false); onStartEdit(msg) }}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                    <Pencil size={13} className="text-violet-400" />
                    Modifica
                  </button>
                )}
                {msg.is_edited && (
                  <button
                    onClick={() => { setShowMenu(false); onShowHistory(msg.id) }}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                    <History size={13} className="text-blue-400" />
                    Storico modifiche
                  </button>
                )}
                {canDeleteAll && (
                  <button
                    onClick={() => { setShowMenu(false); onDeleteAll(msg) }}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors">
                    <Trash2 size={13} />
                    Elimina per tutti
                  </button>
                )}
                <button
                  onClick={() => { setShowMenu(false); onDeleteMe(msg) }}
                  className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-700 hover:text-white transition-colors">
                  <Trash2 size={13} />
                  Elimina per me
                </button>
              </div>
            )}
          </div>
        )}

        <div className={`max-w-xs sm:max-w-md lg:max-w-lg px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words ${
          msg.is_deleted
            ? 'bg-gray-800/50 border border-gray-700 text-gray-500 italic'
            : isMine
              ? 'bg-violet-600 text-white rounded-br-sm'
              : 'bg-gray-800 text-gray-200 rounded-bl-sm'
        }`}>
          {msg.is_deleted ? (
            <span>🚫 Messaggio eliminato</span>
          ) : isEditing ? (
            <div>
              <textarea
                autoFocus
                value={editText}
                onChange={e => onEditChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEditSave() }
                  if (e.key === 'Escape') onEditCancel()
                }}
                className="w-full bg-violet-700 text-white placeholder-violet-300 rounded-lg px-2 py-1 text-sm resize-none focus:outline-none min-w-[200px]"
                rows={Math.max(1, editText.split('\n').length)}
              />
              <div className="flex items-center gap-2 mt-2">
                <button onClick={onEditSave} className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded-lg transition-colors">
                  <Check size={11} /> Salva
                </button>
                <button onClick={onEditCancel} className="text-xs text-violet-200 hover:text-white transition-colors">Annulla</button>
              </div>
            </div>
          ) : (
            <>
              <span className="whitespace-pre-wrap">{msg.contenuto}</span>
              <div className={`flex items-center gap-1.5 justify-end mt-1 ${isMine ? 'text-violet-200' : 'text-gray-500'}`}>
                {msg.is_edited && (
                  <button
                    onClick={() => onShowHistory(msg.id)}
                    className="text-[10px] italic hover:underline opacity-70 hover:opacity-100 transition-opacity">
                    (modificato)
                  </button>
                )}
                <span className="text-[10px]">{fmtTime(msg.created_at)}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

/* ─── Main ─── */
const Chat = () => {
  const { profile: myProfile } = useAuth()
  const [profiles, setProfiles] = useState([])
  const [community, setCommunity] = useState([])
  const [showCommunity, setShowCommunity] = useState(false)
  const [conversations, setConversations] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [messages, setMessages] = useState([])
  const [testo, setTesto] = useState('')
  const [sending, setSending] = useState(false)
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editText, setEditText] = useState('')
  const [editHistory, setEditHistory] = useState([])
  const [showHistoryId, setShowHistoryId] = useState(null)
  const [botUnread, setBotUnread] = useState(0)
  const [botLastMsg, setBotLastMsg] = useState(null)
  const [botTyping, setBotTyping] = useState(false)
  const [chatW, setChatW] = useState(() => parseInt(localStorage.getItem('chatPanelW') || '288', 10))
  const bottomRef = useRef(null)
  const channelRef = useRef(null)
  const selectedUserIdRef = useRef(null)
  const buildConversationsRef = useRef(null)
  const chatDragRef = useRef(false)

  const myId = myProfile?.id

  /* ── Chiama run_bot_checks al caricamento ── */
  useEffect(() => {
    if (!myId) return
    const run = async () => { try { await supabase.rpc('run_bot_checks', { p_user_id: myId }) } catch (e) {} }
    run()
  }, [myId])

  /* fetch profili — solo colleghi di progetto + conversazioni esistenti */
  useEffect(() => {
    if (!myId) return
    const load = async () => {
      const isAdmin = myProfile?.role === 'admin'
      const isClient = myProfile?.role === 'client'

      const { data: all } = await supabase.from('profiles')
        .select('id, full_name, nickname, avatar_url, role, ruolo_creativo')
        .neq('id', myId)
        .order('full_name', { nullsFirst: false })
      const tutti = all || []

      if (isAdmin) {
        // Admin vede tutti direttamente nella lista chat
        setProfiles(tutti)
        setCommunity([])
        return
      }

      // Colleghi: membri dei miei stessi progetti
      const { data: myMemb } = await supabase
        .from('project_members').select('project_id').eq('user_id', myId)
      const pids = (myMemb || []).map(r => r.project_id)

      const visibleIds = new Set()
      if (pids.length > 0) {
        const { data: membri } = await supabase
          .from('project_members').select('user_id').in('project_id', pids)
        ;(membri || []).forEach(r => { if (r.user_id !== myId) visibleIds.add(r.user_id) })
      }

      // Non perdere conversazioni già avviate
      const { data: msgs } = await supabase.from('chat_messaggi')
        .select('sender_id, receiver_id')
        .or(`sender_id.eq.${myId},receiver_id.eq.${myId}`)
        .not('sender_id', 'is', null)
      ;(msgs || []).forEach(m => {
        const partner = m.sender_id === myId ? m.receiver_id : m.sender_id
        if (partner) visibleIds.add(partner)
      })

      setProfiles(tutti.filter(p => visibleIds.has(p.id)))
      // Community: tutti i creativi iscritti (esclusi clienti) — solo per i membri, non per i clienti
      setCommunity(isClient ? [] : tutti.filter(p => p.role !== 'client' && !visibleIds.has(p.id)))
    }
    load()
  }, [myId, myProfile?.role])

  /* fetch metadata bot (ultimo msg + unread) */
  const fetchBotMeta = useCallback(async () => {
    if (!myId) return
    try {
      const [{ data: last }, { count }] = await Promise.all([
        supabase.from('chat_messaggi')
          .select('*')
          .is('sender_id', null)
          .eq('receiver_id', myId)
          .order('created_at', { ascending: false })
          .limit(1),
        supabase.from('chat_messaggi')
          .select('id', { count: 'exact', head: true })
          .is('sender_id', null)
          .eq('receiver_id', myId)
          .is('letto_at', null),
      ])
      setBotLastMsg(last?.[0] || null)
      setBotUnread(count || 0)
    } catch (e) {
      // Supabase error: ignore silently
    }
  }, [myId])

  /* build conversazioni (esclude msg bot sender_id=null) */
  const buildConversations = useCallback(async () => {
    if (!myId || profiles.length === 0) return
    try {
      const { data: msgs } = await supabase
        .from('chat_messaggi')
        .select('id, sender_id, receiver_id, contenuto, created_at, letto_at, is_deleted, hidden_for_sender, hidden_for_receiver')
        .or(`sender_id.eq.${myId},receiver_id.eq.${myId}`)
        .not('sender_id', 'is', null)   // escludi messaggi bot
        .order('created_at', { ascending: false })
      if (!msgs) return

      const map = {}
      msgs.forEach(m => {
        if (m.hidden_for_sender && m.sender_id === myId) return
        if (m.hidden_for_receiver && m.receiver_id === myId) return
        const partner = m.sender_id === myId ? m.receiver_id : m.sender_id
        if (!map[partner]) map[partner] = { lastMessage: m, unread: 0 }
        if (m.receiver_id === myId && !m.letto_at && !m.is_deleted) map[partner].unread++
      })
      profiles.forEach(p => { if (!map[p.id]) map[p.id] = { lastMessage: null, unread: 0 } })
      const list = profiles.map(p => ({
        other: p,
        lastMessage: map[p.id]?.lastMessage ?? null,
        unread: map[p.id]?.unread ?? 0,
      }))
      list.sort((a, b) => {
        if (a.lastMessage && b.lastMessage)
          return new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
        if (a.lastMessage) return -1
        if (b.lastMessage) return 1
        const na = a.other?.nickname || a.other?.full_name || ''
        const nb = b.other?.nickname || b.other?.full_name || ''
        return na.localeCompare(nb)
      })
      setConversations(list)
      fetchBotMeta().catch(() => {})
    } catch (e) {
      // Supabase error: ignore silently
    }
  }, [myId, profiles, fetchBotMeta])

  useEffect(() => { selectedUserIdRef.current = selectedUserId }, [selectedUserId])
  useEffect(() => { buildConversationsRef.current = buildConversations }, [buildConversations])
  useEffect(() => { buildConversations() }, [buildConversations])

  /* fetch messaggi */
  const fetchMessages = useCallback(async () => {
    if (!myId || !selectedUserId) return
    try {
      if (selectedUserId === BOT_ID) {
        const { data } = await supabase
          .from('chat_messaggi')
          .select('*')
          .or(`and(sender_id.eq.${myId},receiver_id.is.null),and(sender_id.is.null,receiver_id.eq.${myId})`)
          .order('created_at', { ascending: true })
        setMessages(data || [])
        await supabase.from('chat_messaggi')
          .update({ letto_at: new Date().toISOString() })
          .is('sender_id', null)
          .eq('receiver_id', myId)
          .is('letto_at', null)
        setBotUnread(0)
        // Prima apertura: il bot si presenta (una volta sola per account)
        if (!data || data.length === 0) {
          setBotTyping(true)
          supabase.functions.invoke('chat-bot', { body: { message: '__intro__' } })
            .then(({ data: res, error }) => { if (res?.skipped || error) setBotTyping(false) })
            .catch(() => setBotTyping(false))
        }
        return
      }

      const { data } = await supabase
        .from('chat_messaggi')
        .select('*')
        .or(
          `and(sender_id.eq.${myId},receiver_id.eq.${selectedUserId}),` +
          `and(sender_id.eq.${selectedUserId},receiver_id.eq.${myId})`
        )
        .order('created_at', { ascending: true })
      setMessages(data || [])
      await supabase.from('chat_messaggi')
        .update({ letto_at: new Date().toISOString() })
        .eq('sender_id', selectedUserId)
        .eq('receiver_id', myId)
        .is('letto_at', null)
      buildConversations().catch(() => {})
    } catch (e) {
      // Supabase error: ignore silently
    }
  }, [myId, selectedUserId, buildConversations])

  useEffect(() => { fetchMessages() }, [fetchMessages])

  /* scroll to bottom */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  /* realtime */
  useEffect(() => {
    if (!myId) return
    if (channelRef.current) supabase.removeChannel(channelRef.current)

    const updateMsg = (updated) => {
      setMessages(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m))
      buildConversationsRef.current?.()
    }

    const ch = supabase.channel(`chat-${myId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'chat_messaggi',
        filter: `receiver_id=eq.${myId}`,
      }, payload => {
        const msg = payload.new
        const isBotMsg = msg.sender_id === null
        const matchesSelected = isBotMsg
          ? selectedUserIdRef.current === BOT_ID
          : msg.sender_id === selectedUserIdRef.current

        if (matchesSelected) {
          setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg])
          supabase.from('chat_messaggi').update({ letto_at: new Date().toISOString() }).eq('id', msg.id)
          if (isBotMsg) { setBotUnread(0); setBotTyping(false) }
        } else if (isBotMsg) {
          setBotUnread(prev => prev + 1)
          setBotLastMsg(msg)
        }
        buildConversationsRef.current?.()
      })
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'chat_messaggi',
        filter: `receiver_id=eq.${myId}`,
      }, payload => updateMsg(payload.new))
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'chat_messaggi',
        filter: `sender_id=eq.${myId}`,
      }, payload => updateMsg(payload.new))
      .subscribe()

    channelRef.current = ch
    return () => { supabase.removeChannel(ch) }
  }, [myId]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Delete per tutti ─── */
  const handleDeleteAll = async (msg) => {
    await supabase.from('chat_messaggi')
      .update({ is_deleted: true, deleted_at: new Date().toISOString(), contenuto: '' })
      .eq('id', msg.id)
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_deleted: true, contenuto: '' } : m))
    buildConversations()
  }

  /* ─── Delete per me ─── */
  const handleDeleteMe = async (msg) => {
    const field = msg.sender_id === myId ? 'hidden_for_sender' : 'hidden_for_receiver'
    await supabase.from('chat_messaggi').update({ [field]: true }).eq('id', msg.id)
    setMessages(prev => prev.filter(m => m.id !== msg.id))
    buildConversations()
  }

  /* ─── Edit ─── */
  const handleStartEdit = (msg) => {
    setEditingId(msg.id)
    setEditText(msg.contenuto)
  }

  const handleSaveEdit = async () => {
    if (!editingId || !editText.trim()) { setEditingId(null); return }
    const original = messages.find(m => m.id === editingId)
    if (!original || editText.trim() === original.contenuto) { setEditingId(null); return }
    await supabase.from('chat_messaggi_edits').insert({
      message_id: editingId, old_content: original.contenuto, edited_by: myId,
    })
    const now = new Date().toISOString()
    await supabase.from('chat_messaggi')
      .update({ contenuto: editText.trim(), is_edited: true, edited_at: now })
      .eq('id', editingId)
    setMessages(prev => prev.map(m =>
      m.id === editingId ? { ...m, contenuto: editText.trim(), is_edited: true, edited_at: now } : m
    ))
    setEditingId(null); setEditText('')
  }

  const handleCancelEdit = () => { setEditingId(null); setEditText('') }

  /* ─── Storico modifiche ─── */
  const handleShowHistory = async (msgId) => {
    const { data } = await supabase.from('chat_messaggi_edits')
      .select('*').eq('message_id', msgId).order('edited_at', { ascending: false })
    setEditHistory(data || [])
    setShowHistoryId(msgId)
  }

  /* ─── Send bot ─── */
  const handleSendBot = async () => {
    if (!testo.trim() || botTyping) return
    const msgText = testo.trim()
    setTesto('')

    // Aggiunta ottimistica in UI
    const tempId = `temp-${Date.now()}`
    const tempMsg = { id: tempId, sender_id: myId, receiver_id: null, contenuto: msgText, created_at: new Date().toISOString(), is_deleted: false }
    setMessages(prev => [...prev, tempMsg])

    // Salva su DB e sostituisci il temp con l'ID reale
    const { data: saved } = await supabase.from('chat_messaggi')
      .insert({ sender_id: myId, receiver_id: null, contenuto: msgText })
      .select().single()
    if (saved) setMessages(prev => prev.map(m => m.id === tempId ? saved : m))

    setBotTyping(true)
    try {
      const { error: fnError } = await supabase.functions.invoke('chat-bot', {
        body: { message: msgText }
      })
      if (fnError) {
        // Estrai il dettaglio dell'errore dalla risposta (usato per debug)
        let detail = fnError.message || 'Errore sconosciuto'
        try {
          const body = await fnError.context?.json?.()
          if (body?.detail) detail = `[HTTP ${body.status}] ${body.detail}`
          else if (body?.error) detail = body.error
        } catch { /* ignore */ }
        throw new Error(detail)
      }
    } catch (e) {
      setMessages(prev => [...prev, {
        id: `err-${Date.now()}`, sender_id: null, receiver_id: myId,
        contenuto: `⚠️ ${e.message}`,
        created_at: new Date().toISOString(), is_deleted: false,
      }])
    } finally {
      setBotTyping(false)
    }
  }

  /* ─── Send ─── */
  const handleSend = async () => {
    if (!testo.trim() || !selectedUserId || sending || selectedUserId === BOT_ID) return

    setSending(true)
    const { data: newMsg } = await supabase.from('chat_messaggi').insert({
      sender_id: myId, receiver_id: selectedUserId, contenuto: testo.trim(),
    }).select().single()
    if (newMsg) setMessages(prev => [...prev, newMsg])
    setTesto(''); setSending(false)
    buildConversations()
  }

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (selectedUserId === BOT_ID) handleSendBot()
      else handleSend()
    }
  }

  const selectedProfile = profiles.find(p => p.id === selectedUserId)
  const selectedNome = selectedProfile?.nickname || selectedProfile?.full_name || ''
  const isBotSelected = selectedUserId === BOT_ID

  const filteredConvs = conversations.filter(c => {
    const nome = (c.other?.nickname || c.other?.full_name || '').toLowerCase()
    return nome.includes(search.toLowerCase())
  })

  const filteredCommunity = community.filter(p => {
    const nome = (p.nickname || p.full_name || '').toLowerCase()
    return nome.includes(search.toLowerCase())
  })

  // Avvia chat con una persona della community: spostala tra i profili visibili
  const handleStartCommunityChat = (p) => {
    setProfiles(prev => prev.some(x => x.id === p.id) ? prev : [...prev, p])
    setCommunity(prev => prev.filter(x => x.id !== p.id))
    setSelectedUserId(p.id)
  }
  const showBot = !search || 'plutonicbot'.includes(search.toLowerCase()) || 'bot'.includes(search.toLowerCase())

  const visibleMessages = messages.filter(m => {
    if (m.hidden_for_sender && m.sender_id === myId) return false
    if (m.hidden_for_receiver && m.receiver_id === myId) return false
    return true
  })

  const msgWithDates = visibleMessages.map((m, i) => {
    const prev = i > 0 ? new Date(visibleMessages[i - 1].created_at).toDateString() : null
    const thisDate = new Date(m.created_at).toDateString()
    return { ...m, showDate: thisDate !== prev, dateLabel: fmtDate(m.created_at) }
  })

  /* ── Chat panel drag ── */
  const handleChatDragStart = useCallback((e) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = chatW
    chatDragRef.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    const onMove = (ev) => {
      const newW = Math.min(480, Math.max(200, startW + ev.clientX - startX))
      setChatW(newW)
    }
    const onUp = (ev) => {
      chatDragRef.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      const finalW = Math.min(480, Math.max(200, startW + ev.clientX - startX))
      setChatW(finalW)
      localStorage.setItem('chatPanelW', String(finalW))
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [chatW])

  return (
    <div className="flex h-[calc(100vh-4rem)] md:h-[calc(100vh-2rem)] -mt-4 -ml-4 md:-ml-8 overflow-hidden rounded-xl border border-gray-800">

      {/* Sidebar conversazioni */}
      <div
        className={`relative flex-shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col md:[width:var(--chat-w)] ${selectedUserId ? 'hidden md:flex' : 'flex w-full'}`}
        style={{ '--chat-w': `${chatW}px` }}
      >
        <div className="px-4 py-4 border-b border-gray-800">
          <h1 className="text-lg font-bold text-white mb-3">Chat</h1>
          <input
            type="text"
            placeholder="Cerca persona..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {/* Bot — sempre in cima */}
          {showBot && (
            <>
              <BotConversationItem
                selected={isBotSelected}
                unread={botUnread}
                lastMsg={botLastMsg}
                onClick={() => setSelectedUserId(BOT_ID)}
              />
              <div className="mx-4 h-px bg-gray-800/60" />
            </>
          )}
          {filteredConvs.length === 0 && !showBot && filteredCommunity.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-8 px-4">Nessun utente trovato.</p>
          ) : filteredConvs.map(c => (
            <ConversationItem key={c.other.id} conv={c} selected={selectedUserId === c.other.id}
              myId={myId} onClick={() => setSelectedUserId(c.other.id)} />
          ))}

          {/* Community — networking con tutti i creativi iscritti */}
          {filteredCommunity.length > 0 && (
            <div className="mt-2 border-t border-gray-800/60 pt-2">
              <button
                onClick={() => setShowCommunity(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Globe size={12} className="text-violet-400" />
                  Community · {filteredCommunity.length}
                </span>
                <ChevronDown size={14} className={`transition-transform ${showCommunity ? 'rotate-180' : ''}`} />
              </button>
              {showCommunity && filteredCommunity.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleStartCommunityChat(p)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800/60 transition-colors text-left"
                >
                  <Avatar profile={p} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-200 truncate">{p.nickname || p.full_name}</p>
                    {p.ruolo_creativo && (
                      <p className="text-xs text-gray-500 truncate">{p.ruolo_creativo}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Drag handle — solo desktop */}
        <div
          className="hidden md:block absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 group"
          onMouseDown={handleChatDragStart}
        >
          <div className="absolute inset-y-0 left-0 right-0 group-hover:bg-violet-500/40 transition-colors rounded-full" />
        </div>
      </div>

      {/* Area messaggi */}
      <div className={`flex-1 flex flex-col bg-gray-950 ${!selectedUserId ? 'hidden md:flex' : 'flex'}`}>
        {!selectedUserId ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <MessageSquare size={48} className="text-gray-800 mb-4" />
            <p className="text-gray-500 text-sm">Seleziona una conversazione<br />per iniziare a chattare</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-800 bg-gray-900">
              <button className="md:hidden text-gray-400 hover:text-white mr-1" onClick={() => setSelectedUserId(null)}>←</button>
              {isBotSelected ? (
                <>
                  <BotAvatar size="md" />
                  <div>
                    <p className="text-white font-medium text-sm">PlutonicBot</p>
                    <div className="flex items-center gap-1.5">
                      <p className="text-indigo-400/70 text-xs">AI Assistant · Task · Progetti</p>
                      {(myProfile?.role === 'admin' || myProfile?.role === 'vip') && (
                        <span className="text-[9px] font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-1.5 py-0.5 rounded-full tracking-wide">BETA</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Avatar profile={selectedProfile} size="md" />
                  <div>
                    <p className="text-white font-medium text-sm">{selectedNome}</p>
                    <p className="text-gray-500 text-xs">{selectedProfile?.ruolo_creativo || 'Membro'}</p>
                  </div>
                </>
              )}
            </div>

            {/* Messaggi */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {msgWithDates.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  {isBotSelected ? (
                    <div className="text-center">
                      <BotAvatar size="md" />
                      <p className="text-gray-500 text-sm mt-3">Ciao! Sono PlutonicBot.</p>
                      <p className="text-gray-700 text-xs mt-1">Chiedimi qualcosa sui tuoi task, progetti o qualsiasi cosa.</p>
                    </div>
                  ) : (
                    <p className="text-gray-600 text-sm">Nessun messaggio ancora. Di' qualcosa! 👋</p>
                  )}
                </div>
              )}
              {msgWithDates.map(m => (
                <MessageBubble
                  key={m.id}
                  msg={m}
                  isMine={m.sender_id === myId}
                  isBot={m.sender_id === null}
                  showDate={m.showDate}
                  dateLabel={m.dateLabel}
                  onDeleteAll={handleDeleteAll}
                  onDeleteMe={handleDeleteMe}
                  onStartEdit={handleStartEdit}
                  onShowHistory={handleShowHistory}
                  isEditing={editingId === m.id}
                  editText={editText}
                  onEditChange={setEditText}
                  onEditSave={handleSaveEdit}
                  onEditCancel={handleCancelEdit}
                />
              ))}
              {isBotSelected && botTyping && (
                <div className="flex gap-2.5 items-end">
                  <BotAvatar size="sm" />
                  <div className="bg-indigo-950/60 border border-indigo-500/20 px-4 py-3 rounded-2xl rounded-bl-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            {isBotSelected ? (
              <div className="px-4 py-3 border-t border-gray-800 bg-gray-900">
                <div className="flex items-end gap-3">
                  <textarea
                    rows={1}
                    value={testo}
                    onChange={e => setTesto(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Scrivi a PlutonicBot..."
                    disabled={botTyping}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors resize-none max-h-32 disabled:opacity-50"
                    style={{ height: '42px' }}
                  />
                  <button
                    onClick={handleSendBot}
                    disabled={!testo.trim() || botTyping}
                    className="flex-shrink-0 w-10 h-10 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 rounded-xl flex items-center justify-center transition-colors"
                  >
                    <Send size={16} className="text-white" />
                  </button>
                </div>
                <p className="text-gray-700 text-xs mt-1.5 ml-1">Invio per inviare · Shift+Invio per nuova riga</p>
              </div>
            ) : (
              <div className="px-4 py-3 border-t border-gray-800 bg-gray-900">
                <div className="flex items-end gap-3">
                  <textarea
                    rows={1}
                    value={testo}
                    onChange={e => setTesto(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Scrivi un messaggio..."
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors resize-none max-h-32"
                    style={{ height: '42px' }}
                  />
                  <button onClick={handleSend} disabled={!testo.trim() || sending}
                    className="flex-shrink-0 w-10 h-10 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 rounded-xl flex items-center justify-center transition-colors">
                    <Send size={16} className="text-white" />
                  </button>
                </div>
                <p className="text-gray-700 text-xs mt-1.5 ml-1">Invio per mandare · Shift+Invio per nuova riga</p>
              </div>
            )}
          </>
        )}
      </div>

      {showHistoryId && (
        <HistoryModal history={editHistory} onClose={() => setShowHistoryId(null)} />
      )}
    </div>
  )
}

export default Chat
